<script lang="ts">
// this must be exported here for the keep alive on reroute
export default {
  name: "Families",
};
</script>

<script setup lang="ts">
import FamiliesTable from "@/components/models/family/FamilyTable.vue";
import { useSnackStore } from "@/stores/snack";
import { useQuery } from "@tanstack/vue-query";
import { definePage } from "unplugin-vue-router/runtime";
import { onMounted, provide, ref } from "vue";

import ChildService, { type TChild } from "@/api/child";
import FamilyService, { type TFamily } from "@/api/family";
import UserService from "@/api/user";
import { ActionsEnum, SubjectsEnum, type AppAbility } from "@/services/ability";
import { type PaginatedType, type VFormFieldChoice, type VFormOptions } from "@/utils/DjangoTypes";
import { useAbility } from "@casl/vue";
import { DefaultServerTableParams, type ServerTableParamsType } from "@/utils/TableUtils";

definePage({
  meta: {
    title: "Families",
    checkPrivileges: () => {
      const { can } = useAbility<AppAbility>();
      return can(ActionsEnum.USE, SubjectsEnum.FAMILY);
    },
  },
});

const snackStore = useSnackStore();
const fetchEnabled = ref(false);
const fetching = ref(true);

const userOptions = ref<VFormOptions>({});
const familiesChoices = ref<VFormFieldChoice<any>[]>([]);
const userChoices = ref<VFormFieldChoice<any>[]>([]);
const childrenChoices = ref<VFormFieldChoice<any>[]>([]);

const queriesTableParams: Record<string, ServerTableParamsType> = {
  families: DefaultServerTableParams,
  children: DefaultServerTableParams,
};
provide("queriesTableParams", queriesTableParams);

const {
  data: families,
  isPending: isPending,
  isFetching: isFetching,
} = useQuery({
  queryKey: ["families"],
  queryFn: async (): Promise<PaginatedType<TFamily[]> | undefined> => {
    try {
      const [result, users, children] = await Promise.all([
        FamilyService.family.getAll(queriesTableParams.families),
        UserService.user.getAllSelectable(),
        ChildService.child.getAll(queriesTableParams.children),
      ]);

      userChoices.value = users;
      childrenChoices.value = children.data.items.map((item: TChild) => {
        return {
          value: item.id,
          title: `${item.name} ${item.surname}`,
        };
      });

      result.data.items = result.data.items.map(family => {
        const childrenIds = children.data.items.filter(child => child.families.includes(family.id)).map(child => child.id);

        return {
          ...family,
          children: childrenIds,
        };
      });

      return result.data;
    } catch (error) {
      snackStore.showError(error);
    }
  },
  enabled: () => fetchEnabled.value,
});

onMounted(async () => {
  fetching.value = false;
  fetchEnabled.value = true;
});
</script>

<template>
  <v-container fluid>
    <div class="tw:!mb-10">
      <families-table
        :query-key="'families'"
        :data="families"
        :loading="isPending || isFetching"
        :families="familiesChoices"
        :users="userChoices"
        :options="userOptions"
        :children="childrenChoices"
      />
    </div>
  </v-container>
</template>

<style scoped></style>
